import QuizIcon from '@mui/icons-material/Quiz';
import {
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  Stack
} from "@mui/material";
import TranslateIcon from '@mui/icons-material/Translate';
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import AppLoader from "../../../components/AppLoader";
import DialogBody from "../../../components/Dialog/Body";
import DialogActionButtons from '../../../components/Dialog/Footer';
import DialogHeader from "../../../components/Dialog/Header";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import {
  createAdminFaq,
  updateAdminFaq,
} from "../../../services/adminFaqApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { translateText } from "../../../utils/translationService";
import { addEditFaqValidation } from '../../../utils/Validation';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const initialInputValues = {
  question_en: "",
  question_ro: "",
  answer_en: "",
  answer_ro: "",
};

const AddEditFaq = forwardRef(({ }, ref) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(["faq", "common"]);
  const [isLoading, setIsLoading] = useState({
    translateToEnglish: false,
    save: false,
  });
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params?.type === "edit") {
        const { question, answer } = params.data;
        setInputValues({
          question_en: question?.en || "",
          question_ro: question?.ro || "",
          answer_en: answer?.en || "",
          answer_ro: answer?.ro || "",
        });
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
  };

  const handleChangeInputs = (e) => {
    const { name, value } = e.target;
    setInputValues((prev) => ({ ...prev, [name]: value }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddEditFaq = () => {
    const valid = addEditFaqValidation(inputValues, setInputError);
    if (valid) return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));

    const payload = {
      question: {
        en: inputValues.question_en,
        ro: inputValues.question_ro,
      },
      answer: {
        en: inputValues.answer_en,
        ro: inputValues.answer_ro,
      },
      type: paramsData?.userType,
    };

    if (paramsData?.type === "add") {
      handleApiCall(createAdminFaq, payload, "");
    } else {
      handleApiCall(updateAdminFaq, payload, paramsData?.data?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading(true);
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData(paramsData?.userType);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };


  const handleTranslateToEnglish = async (key1, key2) => {
    if (!inputValues[key1]) {
      return handleOpenSnackBar("error", t("validation.valueRequired", { ns: "common" }));
    }

    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  return (
    <React.Fragment>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog onClose={handleClose} open={open}>
        <DialogHeader
          title={paramsData?.type === "add" ? t("addFaq") : t("editFaq")}
          secondaryHeading={paramsData?.type === "add" ? t("addFaqDescription") : t("editFaqDescription")}
          icon={<QuizIcon />}
          onClose={handleClose}
        />

        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("questionInRomanian")}
                htmlFor={`question_ro`}
              />
              <TextInput
                id={`question_ro`}
                required
                name={`question_ro`}
                placeholder={t("enterQuestionInRomanian")}
                value={inputValues.question_ro}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.question_ro)}
                helperText={t(inputError.question_ro)}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" mb={"5px"}>
                <Label
                  required
                  labelText={t("questionInEnglish")}
                  htmlFor={`question-en`}
                  labelProps={{
                    marginBottom: 0,
                  }}
                />
                <Chip
                  size="small"
                  label={t("translateToEnglish", { ns: "common" })}
                  onClick={() => handleTranslateToEnglish("question_ro", "question_en")}
                  disabled={isLoading.translateToEnglish}
                  icon={isLoading.translateToEnglish ? <CircularProgress size={16} color="primary" /> : <TranslateIcon />}
                />
              </Stack>
              <TextInput
                id={`question_en`}
                required
                name={`question_en`}
                placeholder={t("enterQuestionInEnglish")}
                value={inputValues.question_en}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.question_en)}
                helperText={t(inputError.question_en)}
              />
            </Grid>


            <Grid item xs={12}>
              <Label
                required
                labelText={t("answerInRomanian")}
                htmlFor={`answer_ro`}
              />
              <TextInput
                id={`answer_ro`}
                required
                name={`answer_ro`}
                placeholder={t("enterAnswerInRomanian")}
                value={inputValues.answer_ro}
                onChange={handleChangeInputs}
                multiline
                rows={4}
                inputError={Boolean(inputError.answer_ro)}
                helperText={t(inputError.answer_ro)}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" mb={"5px"}>

                <Label
                  required
                  labelText={t("answerInEnglish")}
                  htmlFor={`answer_en`}
                  labelProps={{
                    marginBottom: 0,
                  }}
                />
                <Chip
                  size="small"
                  label={t("translateToEnglish", { ns: "common" })}
                  onClick={() => handleTranslateToEnglish("answer_ro", "answer_en")}
                  disabled={isLoading.translateToEnglish}
                  icon={isLoading.translateToEnglish ? <CircularProgress size={16} color="primary" /> : <TranslateIcon />}
                />
              </Stack>
              <TextInput
                id={`answer_en`}
                required
                name={`answer_en`}
                placeholder={t("enterAnswerInEnglish")}
                value={inputValues.answer_en}
                onChange={handleChangeInputs}
                multiline
                rows={4}
                inputError={Boolean(inputError.answer_en)}
                helperText={t(inputError.answer_en)}
              />
            </Grid>
          </Grid>

        </DialogBody>

        <DialogActionButtons
          onConfirm={handleAddEditFaq}
          onCancel={handleClose}
          confirmText="save"
          cancelText="cancel"
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />


      </BootstrapDialog>
    </React.Fragment>
  );
});

export default AddEditFaq;
